import React, { useEffect, useState, useRef } from "react";
import { Bar, Line } from "react-chartjs-2";
import { Chart, registerables } from "chart.js";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import DateRangePicker from "react-bootstrap-daterangepicker";
import "bootstrap-daterangepicker/daterangepicker.css";
import moment from "moment";
import DefaultImage from "../assets/img/icon1.png";
import UserImage from "../assets/img/profile.png";
import "bootstrap/dist/css/bootstrap.min.css";
import "./Dashboard.css";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { API_URL } from "../config.js";
import LoadingSpinner from "../components/LoadingSpinner.jsx";
import { showToast } from "../components/toast.jsx";
import { Activity, Target, Users, TrendingUp, IndianRupee, PieChart as PieChartIcon, Percent, AlertTriangle, CheckCircle, XCircle } from 'lucide-react';
import { PermissionWrapper, hasPermission } from "../helpers/permission.js";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCrown,
  faArrowTrendUp,
  faArrowTrendDown,
  faChartLine,
  faUsers,
  faHandHoldingDollar,
  faPercent,
  faCalendarAlt
} from "@fortawesome/free-solid-svg-icons";


Chart.register(...registerables);

const LeaderboardSkeleton = () => (
  <>
    <div className="top-performers-row">
      {[0].map((rank) => (
        <div key={rank} className={`performer-card rank-${rank + 1}`}>
          <div className="skeleton-text mb-4" style={{ width: '80px', height: '16px' }}></div>
          <div className={`avatar-wrapper ${rank === 0 ? 'large' : ''}`}>
            <div className="skeleton-card rounded-circle" style={{ width: rank === 0 ? '110px' : '90px', height: rank === 0 ? '110px' : '90px' }}></div>
          </div>
          <div className="skeleton-text mt-3" style={{ width: '120px', height: '18px' }}></div>
          <div className="skeleton-text mt-2" style={{ width: '80px', height: '14px' }}></div>
          <div className="revenue-badge bg-light mt-4" style={{ width: '100%', height: '50px' }}></div>
        </div>
      ))}
    </div>
  </>
);

const ExecutiveSummarySkeleton = () => (
  <div className="summary-table">
    <div className="summary-header">
      <div className="summary-col-metric">Metric</div>
      <div className="summary-col-today">Today</div>
      <div className="summary-col-mtd">MTD</div>
      <div className="summary-col-target">Target</div>
      <div className="summary-col-status">Status</div>
    </div>
    <div className="d-flex flex-column">
      {[1, 2, 3, 4, 5].map((idx) => (
        <div key={idx} className="summary-row py-3">
          <div className="summary-col-metric">
            <div className="skeleton-card rounded-circle" style={{ width: '32px', height: '32px' }}></div>
            <div className="skeleton-text ms-3" style={{ width: '120px', height: '16px' }}></div>
          </div>
          <div className="summary-col-today">
            <div className="skeleton-text" style={{ width: '60px', height: '16px' }}></div>
          </div>
          <div className="summary-col-mtd">
            <div className="skeleton-text" style={{ width: '60px', height: '16px' }}></div>
          </div>
          <div className="summary-col-target">
            <div className="skeleton-text" style={{ width: '60px', height: '16px' }}></div>
          </div>
          <div className="summary-col-status">
            <div className="skeleton-text" style={{ width: '80px', height: '16px' }}></div>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const ExecutivePerformanceSkeleton = () => (
  <div className="table-responsive">
    <table className="table border-0 mb-0">
      <thead>
        <tr className="bg-light bg-opacity-50">
          <th className="border-0 rounded-start text-muted small fw-bold text-uppercase py-3 ps-4">Rank</th>
          <th className="border-0 text-muted small fw-bold text-uppercase py-3">Executive</th>
          <th className="border-0 text-center text-muted small fw-bold text-uppercase py-3">New Leads</th>
          <th className="border-0 text-center text-muted small fw-bold text-uppercase py-3">Contacted</th>
          <th className="border-0 text-center text-muted small fw-bold text-uppercase py-3">Prospects</th>
          <th className="border-0 text-center text-muted small fw-bold text-uppercase py-3">Conversions</th>
          <th className="border-0 text-end text-muted small fw-bold text-uppercase py-3">Revenue</th>
          <th className="border-0 text-end text-muted small fw-bold text-uppercase py-3">Incentive</th>
          <th className="border-0 rounded-end text-center text-muted small fw-bold text-uppercase py-3 pe-4">Compl. Score</th>
        </tr>
      </thead>
      <tbody>
        {[1, 2, 3, 4, 5].map((idx) => (
          <tr key={idx} className="border-bottom border-light">
            <td className="ps-4 py-3"><div className="skeleton-card rounded-circle" style={{ width: '28px', height: '28px' }}></div></td>
            <td className="py-3"><div className="skeleton-text" style={{ width: '140px', height: '16px' }}></div></td>
            <td className="text-center py-3"><div className="skeleton-text" style={{ width: '40px', height: '16px' }}></div></td>
            <td className="text-center py-3"><div className="skeleton-text" style={{ width: '40px', height: '16px' }}></div></td>
            <td className="text-center py-3"><div className="skeleton-text" style={{ width: '60px', height: '16px' }}></div></td>
            <td className="text-center py-3"><div className="skeleton-text" style={{ width: '60px', height: '16px' }}></div></td>
            <td className="text-end py-3"><div className="skeleton-text" style={{ width: '80px', height: '16px' }}></div></td>
            <td className="text-end py-3"><div className="skeleton-text" style={{ width: '80px', height: '16px' }}></div></td>
            <td className="pe-4 py-3"><div className="skeleton-rect w-100" style={{ height: '8px', borderRadius: '4px' }}></div></td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const StatCard = ({ title, value, subtitle, icon: Icon, trend, variant = "default" }) => (
  <div className={`stat-card stat-card-${variant}`}>
    <div className="stat-card-header">
      <h3 className="stat-card-title">{title}</h3>
      {Icon && <Icon className="stat-card-icon text-primary" />}
    </div>
    <div className="stat-card-value">{value}</div>
    {trend && (
      <div className="stat-card-trend-container">
        <span className={`stat-card-trend ${trend.isPositive ? 'text-success' : 'text-danger'}`}>
          {trend.value}
        </span>
        <span className="stat-card-subtitle" style={{ marginLeft: '4px' }}>{subtitle}</span>
      </div>
    )}
    {!trend && subtitle && <p className="stat-card-subtitle">{subtitle}</p>}
  </div>
);

const ChartSkeleton = () => (
  <div className="col-md-4 mainbox">
    <div className="panel panel-default with-rounded-corner with-shadow chartbox p-4">
      <div className="skeleton-text mb-4" style={{ width: '60%', height: '20px' }}></div>
      <div className="skeleton-rect w-100 mb-4" style={{ height: '180px', borderRadius: '8px' }}></div>
      <div className="skeleton-rect w-100" style={{ height: '20px', borderRadius: '4px' }}></div>
    </div>
  </div>
);

const leadDisposition = [
  { status: "New", count: 1000, percentage: 69.6, healthCheck: "Good - Fresh leads", color: "hsl(217, 91%, 60%)" },
  { status: "Follow-Up", count: 600, percentage: 41.8, healthCheck: "Active engagement", color: "hsl(160, 84%, 39%)" },
  { status: "Prospect", count: 180, percentage: 12.5, healthCheck: "Pipeline building", color: "hsl(280, 65%, 60%)" },
  { status: "Subscribed", count: 50, percentage: 3.5, healthCheck: "Revenue generating", color: "hsl(160, 84%, 50%)" },
  { status: "Not Interested", count: 120, percentage: 8.4, healthCheck: "Needs pitch review", color: "hsl(38, 92%, 50%)" },
  { status: "No Response", count: 180, percentage: 12.5, healthCheck: "Follow-up gaps", color: "hsl(0, 84%, 60%)" },
  { status: "Junk", count: 125, percentage: 8.7, healthCheck: "Lead source quality", color: "hsl(0, 0%, 50%)" },
];

const LEAD_COLORS = [
  "hsl(217, 91%, 60%)",
  "hsl(160, 84%, 39%)",
  "hsl(280, 65%, 60%)",
  "hsl(160, 84%, 50%)",
  "hsl(38, 92%, 50%)",
  "hsl(0, 84%, 60%)",
  "hsl(0, 0%, 40%)",
];

const LeadDispositionChart = ({ dispositionData }) => {
  const [activeIndex, setActiveIndex] = useState(null);

  const formattedData = dispositionData?.disposition?.map(item => ({
    ...item,
    healthCheck: item.description
  })) || leadDisposition;

  const totalLeads = dispositionData?.total_leads || formattedData.reduce((sum, item) => sum + item.count, 0);

  const onPieEnter = (_, index) => {
    setActiveIndex(index);
  };

  const onPieLeave = () => {
    setActiveIndex(null);
  };

  return (
    <div className="panel panel-default with-rounded-corner with-shadow mb-5">
      <div className="panel-heading">
        <div className="d-flex align-items-center gap-3">
          <div className="p-2 rounded-4" style={{ background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(16, 185, 129, 0.2) 100%)' }}>
            <PieChartIcon className="text-success" size={20} />
          </div>
          <div>
            <h4 className="mb-0 fw-bold text-dark" style={{ fontSize: '1.1rem' }}>Lead Disposition</h4>
            <span className="text-muted small">Distribution across all pipeline stages</span>
          </div>
        </div>
      </div>
      <div className="panel-body p-4">

        <div className="row align-items-center g-4">
          {/* Chart Section */}
          <div className="col-lg-6 position-relative">
            <div style={{ height: '300px', width: '100%' }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={formattedData}
                    cx="50%"
                    cy="50%"
                    innerRadius={80}
                    outerRadius={110}
                    paddingAngle={3}
                    dataKey="count"
                    nameKey="status"
                    onMouseEnter={onPieEnter}
                    onMouseLeave={onPieLeave}
                    stroke="none"
                  >
                    {formattedData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={LEAD_COLORS[index % LEAD_COLORS.length]}
                        style={{
                          filter: activeIndex === index ? 'drop-shadow(0px 0px 10px rgba(0,0,0,0.2))' : 'none',
                          transform: activeIndex === index ? 'scale(1.02)' : 'scale(1)',
                          transformOrigin: 'center',
                          transition: 'all 0.3s ease',
                          opacity: activeIndex !== null && activeIndex !== index ? 0.6 : 1
                        }}
                      />
                    ))}
                    {/* Inner Label for Total */}
                    <text x="50%" y="50%" textAnchor="middle" dominantBaseline="middle">
                      <tspan x="50%" dy="-10" fontSize="14" fill="#64748b">Total Leads</tspan>
                      <tspan x="50%" dy="25" fontSize="24" fontWeight="bold" fill="#1e293b">{totalLeads.toLocaleString()}</tspan>
                    </text>
                  </Pie>
                  <Tooltip
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        const data = payload[0].payload;
                        return (
                          <div className="custom-tooltip glass-effect p-3 rounded-3 shadow-lg border-0"
                            style={{ background: 'rgba(255, 255, 255, 0.95)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.5)' }}>
                            <p className="mb-1 fw-bold" style={{ color: data.color }}>{data.status}</p>
                            <p className="mb-0 text-dark fs-5 fw-bolder">{data.count.toLocaleString()}</p>
                            <p className="mb-0 text-muted small">{data.percentage}% of total</p>
                            <div className="mt-2 pt-2 border-top border-light">
                              <span className="badge bg-light text-dark border">{data.healthCheck}</span>
                            </div>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Legend Section */}
          <div className="col-lg-6">
            <div className="d-flex flex-column gap-3 pe-lg-4">
              {formattedData.map((item, index) => (
                <div
                  key={item.status}
                  className="d-flex align-items-center justify-content-between p-2 rounded-3 transition-bg"
                  style={{
                    backgroundColor: activeIndex === index ? 'rgba(0,0,0,0.03)' : 'transparent',
                    cursor: 'default',
                    transition: 'background-color 0.2s ease'
                  }}
                  onMouseEnter={() => setActiveIndex(index)}
                  onMouseLeave={() => setActiveIndex(null)}
                >
                  <div className="d-flex align-items-center gap-3">
                    <div className="position-relative">
                      <div
                        className="rounded-circle shadow-sm"
                        style={{ backgroundColor: LEAD_COLORS[index], width: '12px', height: '12px' }}
                      />
                      {activeIndex === index && (
                        <div
                          className="position-absolute top-50 start-50 translate-middle rounded-circle"
                          style={{
                            width: '20px',
                            height: '20px',
                            border: `2px solid ${LEAD_COLORS[index]}`,
                            opacity: 0.3
                          }}
                        />
                      )}
                    </div>
                    <div>
                      <span className="d-block text-sm fw-semibold text-dark">{item.status}</span>
                      <span className="d-block text-xs text-muted" style={{ fontSize: '0.75rem' }}>{item.healthCheck}</span>
                    </div>
                  </div>
                  <div className="text-end">
                    <span className="d-block text-sm fw-bold text-dark">{item.count.toLocaleString()}</span>
                    <span className="d-block text-xs text-muted">{item.percentage}%</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="mt-4 pt-3 border-top border-light text-center">
          <p className="text-muted text-xs mb-0">
            <Activity className="w-3 h-3 me-1 d-inline" />
            Data updated continuously. Last sync: Just now
          </p>
        </div>
      </div>
    </div>
  );
};

const ExecutivePerformanceTable = ({ data }) => {
  return (
    <div className="panel panel-default with-rounded-corner with-shadow mb-5">
      <div className="panel-heading">
        <div className="d-flex align-items-center gap-3">
          <div className="p-2 rounded-4" style={{ background: 'linear-gradient(135deg, rgba(52, 143, 226, 0.1) 0%, rgba(52, 143, 226, 0.2) 100%)' }}>
            <TrendingUp className="text-primary" size={20} />
          </div>
          <div>
            <h4 className="mb-0 fw-bold text-dark" style={{ fontSize: '1.1rem' }}>Executive Performance</h4>
            <span className="text-muted small">Daily performance metric breakdown</span>
          </div>
        </div>
      </div>
      <div className="panel-body p-4">
        {data === null ? <ExecutivePerformanceSkeleton /> : (
          <div className="table-responsive">
            <table className="table table-hover border-0 mb-0">
              <thead>
                <tr className="bg-light bg-opacity-50">
                  <th className="border-0 rounded-start text-muted small fw-bold text-uppercase py-3 ps-4">Rank</th>
                  <th className="border-0 text-muted small fw-bold text-uppercase py-3">Executive</th>
                  <th className="border-0 text-center text-muted small fw-bold text-uppercase py-3">New Leads</th>
                  <th className="border-0 text-center text-muted small fw-bold text-uppercase py-3">Contacted</th>
                  <th className="border-0 text-center text-muted small fw-bold text-uppercase py-3">Prospects</th>
                  <th className="border-0 text-center text-muted small fw-bold text-uppercase py-3">Conversions</th>
                  <th className="border-0 text-end text-muted small fw-bold text-uppercase py-3">Revenue</th>
                  <th className="border-0 text-end text-muted small fw-bold text-uppercase py-3">Incentive</th>
                  <th className="border-0 rounded-end text-center text-muted small fw-bold text-uppercase py-3 pe-4">Compl. Score</th>
                </tr>
              </thead>
              <tbody>
                {data && data.length > 0 ? (
                  data.map((item, index) => (
                    <tr key={index} className="align-middle border-bottom border-light hover-bg-light transition-all">
                      <td className="fw-bold py-3 ps-4">
                        <span className={`badge rounded-circle d-inline-flex align-items-center justify-content-center ${item.rank === 1 ? 'bg-warning text-white' : item.rank === 2 ? 'bg-secondary text-white' : 'bg-light text-dark'}`}
                          style={{ width: '28px', height: '28px', backgroundColor: item.rank === 3 ? '#cd7f32' : (item.rank > 3 ? '#f1f5f9' : undefined), color: item.rank === 3 ? '#fff' : undefined }}>
                          {item.rank}
                        </span>
                      </td>
                      <td className="fw-semibold text-dark py-3">{item.executiveName}</td>
                      <td className="text-center py-3">
                        <span className="text-dark fw-medium">{item.newLeads}</span>
                      </td>
                      <td className="text-center py-3">
                        <span className="text-muted">{item.contacted}</span>
                      </td>
                      <td className="text-center py-3">
                        <span className="badge rounded-pill px-3" style={{ backgroundColor: 'rgba(52, 143, 226, 0.1)', color: '#348fe2' }}>
                          {item.prospectsCreated}
                        </span>
                      </td>
                      <td className="text-center py-3">
                        <span className="badge rounded-pill px-3" style={{ backgroundColor: 'rgba(16, 185, 129, 0.1)', color: '#10b981' }}>
                          {item.paidConversions}
                        </span>
                      </td>
                      <td className="text-end py-3 fw-bold text-dark">₹{item.revenue.toLocaleString()}</td>
                      <td className="text-end py-3 text-success fw-semibold">₹{item.incentiveEarned.toLocaleString()}</td>
                      <td className="text-center py-3 pe-4">
                        <div className="d-flex align-items-center justify-content-center gap-2">
                          <div className="progress flex-grow-1" style={{ height: '6px', minWidth: '40px', maxWidth: '60px', borderRadius: '10px' }}>
                            <div className="progress-bar bg-info"
                              role="progressbar"
                              style={{ width: `${Math.min(item.complianceScore * 10, 100)}%` }}
                              aria-valuenow={item.complianceScore}
                              aria-valuemin="0"
                              aria-valuemax="10">
                            </div>
                          </div>
                          <span className="small text-muted fw-bold" style={{ fontSize: '0.75rem' }}>{item.complianceScore}</span>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="9" className="text-center py-5 text-muted">
                      No executive performance records found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const ExecutiveMetrics = ({ salesMetrics }) => {
  const data = salesMetrics || {};

  const parseValue = (val) => {
    if (!val) return 0;
    if (typeof val === 'number') return val;
    let num = parseFloat(val);
    const strVal = val.toString().toLowerCase();
    if (strVal.includes('l')) num *= 100000;
    else if (strVal.includes('k')) num *= 1000;
    return num;
  };

  const getMetricStatus = (metric, status, today, target) => {
    if (status === "Action Required") return "danger";
    if (status === "Below Target") return "warning";
    if (status === "On Track") return "success";
    if (status === "--") return "neutral";

    // Only calculate fallback if status is explicitly missing or unknown
    if (!status || status === "Unknown") {
      if (metric === "Compliance Breaches") {
        const breaches = parseInt(today) || 0;
        return breaches > 0 ? "danger" : "success";
      }

      const tVal = parseValue(today);
      const tarVal = parseValue(target);

      if (tarVal === 0 || isNaN(tarVal)) return "success";
      return tVal >= tarVal ? "success" : "warning";
    }

    return "neutral";
  };

  const metricsData = [
    {
      metric: "Active Sales Execs",
      today: data.activeSalesExecs?.today || 0,
      mtd: "—",
      target: data.activeSalesExecs?.target || "0",
      icon: <Users size={18} />,
      apiStatus: data.activeSalesExecs?.status,
      status: getMetricStatus("Active Sales Execs", data.activeSalesExecs?.status, data.activeSalesExecs?.today, data.activeSalesExecs?.target),
    },
    {
      metric: "Total Revenue",
      today: data.totalRevenue?.today || "0",
      mtd: data.totalRevenue?.mtd || "0",
      target: data.totalRevenue?.target || "50L",
      icon: <IndianRupee size={18} />,
      apiStatus: data.totalRevenue?.status,
      status: getMetricStatus("Total Revenue", data.totalRevenue?.status, data.totalRevenue?.today, data.totalRevenue?.target),
    },
    {
      metric: "Avg Revenue / Exec",
      today: data.avgRevenuePerExec?.today || "0",
      mtd: data.avgRevenuePerExec?.mtd || "0",
      target: data.avgRevenuePerExec?.target || "3.57L",
      icon: <TrendingUp size={18} />,
      apiStatus: data.avgRevenuePerExec?.status,
      status: getMetricStatus("Avg Revenue / Exec", data.avgRevenuePerExec?.status, data.avgRevenuePerExec?.today, data.avgRevenuePerExec?.target),
    },
    {
      metric: "Top 20% Segment",
      today: data.top20Contribution?.today || "0%",
      mtd: data.top20Contribution?.mtd || "—",
      target: data.top20Contribution?.target || "≥60%",
      icon: <Percent size={18} />,
      apiStatus: data.top20Contribution?.status,
      status: getMetricStatus("Top 20% Segment", data.top20Contribution?.status, data.top20Contribution?.today, data.top20Contribution?.target),
    },
    {
      metric: "Compliance Breaches",
      today: data.complianceBreaches?.today ?? 0,
      mtd: data.complianceBreaches?.mtd ?? 0,
      target: data.complianceBreaches?.target || 0,
      icon: <AlertTriangle size={18} />,
      apiStatus: data.complianceBreaches?.status,
      status: getMetricStatus("Compliance Breaches", data.complianceBreaches?.status, data.complianceBreaches?.today, data.complianceBreaches?.target),
    },
  ];

  const onTargetCount = metricsData.filter(m => m.status === "success").length;
  const needsAttentionCount = metricsData.filter(m => m.status !== "success").length;

  const getStatusIndicator = (status) => {
    switch (status) {
      case "success":
        return <CheckCircle size={16} className="text-success" />;
      case "warning":
        return <div className="status-dot bg-warning animate-pulse-warning" />;
      case "danger":
        return <XCircle size={16} className="text-danger" />;
      default:
        return null;
    }
  };

  return (
    <div className="panel panel-default with-rounded-corner with-shadow mb-5">
      <div className="panel-heading">
        <div className="d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center gap-3">
            <div className="p-2 rounded-4" style={{ background: 'linear-gradient(135deg, rgba(52, 143, 226, 0.1) 0%, rgba(52, 143, 226, 0.2) 100%)' }}>
              <TrendingUp className="text-primary" size={20} />
            </div>
            <div>
              <h4 className="mb-0 fw-bold text-dark" style={{ fontSize: '1.1rem' }}>Performances Summary</h4>
              <span className="text-muted text-xs fw-600 text-uppercase letter-spacing-1">Performance Benchmarks</span>
            </div>
          </div>
        </div>
      </div>

      <div className="panel-body p-4">
        {!salesMetrics ? <ExecutiveSummarySkeleton /> : (
          <>
            <div className="summary-table">
              {/* Header */}
              <div className="summary-header">
                <div className="summary-col-metric">Metric</div>
                <div className="summary-col-today">Today</div>
                <div className="summary-col-mtd">MTD</div>
                <div className="summary-col-target">Target</div>
                <div className="summary-col-status">Status</div>
              </div>

              {/* Rows */}
              <div className="d-flex flex-column">
                {metricsData.map((row, index) => (
                  <div key={index} className="summary-row">
                    <div className="summary-col-metric">
                      <div className="summary-icon-wrapper">
                        {row.icon}
                      </div>
                      <span className="summary-label">{row.metric}</span>
                    </div>

                    <div className="summary-col-today fw-bold text-dark">
                      {row.today}
                    </div>

                    <div className="summary-col-mtd text-muted small">
                      {row.mtd}
                    </div>

                    <div className="summary-col-target fw-bold text-dark">
                      {row.target}
                    </div>

                    <div className="summary-col-status">
                      <div className="d-flex align-items-center gap-2">
                        {getStatusIndicator(row.status)}
                        <span className="text-xs text-uppercase fw-bold" style={{ color: row.status === 'success' ? '#10b981' : row.status === 'danger' ? '#ef4444' : row.status === 'warning' ? '#f59e0b' : '#64748b', fontSize: '10px' }}>
                          {row.apiStatus ? row.apiStatus : (row.status === 'success' ? 'On Track' : row.status === 'danger' ? 'Critical' : row.status === 'warning' ? 'Below Target' : 'N/A')}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Footer Stats Cards */}
            <div className="row g-3 mt-4">
              <div className="col-md-6">
                <div className="footer-stat-card bg-success-light">
                  <h3 className="mb-1 fw-bold">{onTargetCount}</h3>
                  <p className="text-xs fw-700 text-uppercase mb-0">On Target</p>
                </div>
              </div>
              <div className="col-md-6">
                <div className="footer-stat-card bg-warning-light">
                  <h3 className="mb-1 fw-bold">{needsAttentionCount}</h3>
                  <p className="text-xs fw-700 text-uppercase mb-0">Needs Attention</p>
                </div>
              </div>
              {/* <div className="col-md-4">
                <div className="footer-stat-card bg-danger-light">
                  <h3 className="mb-1 fw-bold">{metricsData.length - onTargetCount - needsAttentionCount}</h3>
                  <p className="text-xs fw-700 text-uppercase mb-0">Total Forecast</p>
                </div>
              </div> */}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const Dashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [stats, setStats] = useState(null);
  const [dispositionData, setDispositionData] = useState(null);
  const [leaderboardData, setLeaderboardData] = useState(null);
  const [executivePerformanceData, setExecutivePerformanceData] = useState(null);
  const [salesMetrics, setSalesMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const toastShownRef = useRef(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const toastParam = params.get("toast");

    if (toastParam === "permission_denied" && !toastShownRef.current) {
      toastShownRef.current = true;
      showToast.error("You Don't Have Enough Permission To Access This Section.");
      params.delete("toast");
      window.history.replaceState({}, "", `${location.pathname}?${params.toString()}`);
    }
  }, [location, navigate]);

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.get(`${API_URL}dashboard`, {
        headers: { Authorization: `Bearer ${token}`, Accept: "*/*" },
      });
      if (response.data) setDashboardData(response.data);
    } catch (err) {
      console.error(err);
      setError("Failed to load dashboard data");
    }
  };

  // Date picker state
  const [dateRange, setDateRange] = useState({
    startDate: moment().subtract(6, "days"),
    endDate: moment(),
  });
  const { startDate, endDate } = dateRange;

  const handleApply = (event, picker) => {
    setDateRange({
      startDate: picker.startDate,
      endDate: picker.endDate,
    });
  };

  const ranges = {
    "Today": [moment(), moment()],
    "Yesterday": [moment().subtract(1, "days"), moment().subtract(1, "days")],
    "Last 7 Days": [moment().subtract(6, "days"), moment()],
    "Last 30 Days": [moment().subtract(29, "days"), moment()],
    "This Month": [moment().startOf("month"), moment().endOf("month")],
    "Last Month": [
      moment().subtract(1, "month").startOf("month"),
      moment().subtract(1, "month").endOf("month"),
    ],
  };

  const formatDate = (date) => {
    if (!date) return "";
    return date.format("YYYY-MM-DD");
  };

  const getQueryParams = () => {
    if (startDate && endDate) {
      return `?startDate=${formatDate(startDate)}&endDate=${formatDate(endDate)}`;
    }
    return "";
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem("authToken");
      const query = getQueryParams();
      const response = await axios.get(`${API_URL}user-dashboard/stats${query}`, {
        headers: { Authorization: `Bearer ${token}`, Accept: "*/*" },
      });
      if (response.data && response.data.success) {
        setStats(response.data.data.stats || response.data.data);
      }
    } catch (err) {
      console.error("Failed to fetch stats", err);
    }
  };

  const fetchDisposition = async () => {
    try {
      const token = localStorage.getItem("authToken");
      const query = getQueryParams();
      const response = await axios.get(`${API_URL}user-dashboard/disposition${query}`, {
        headers: { Authorization: `Bearer ${token}`, Accept: "*/*" },
      });
      if (response.data && response.data.success) {
        setDispositionData(response.data.data);
      }
    } catch (err) {
      console.error("Failed to fetch disposition", err);
    }
  };

  const fetchTopPerformers = async () => {
    try {
      const token = localStorage.getItem("authToken");
      const query = getQueryParams();

      const response = await axios.get(`${API_URL}user-dashboard/stats-executive-performance${query}`, {
        headers: { Authorization: `Bearer ${token}`, Accept: "*/*" },
      });

      if (response.data && response.data.success) {
        const data = response.data.data;
        let formatted = Array.isArray(data) ? data : [];

        // Map and sanitize the data
        const mappedData = formatted.map((item, index) => ({
          ...item,
          id: item.id || index,
          photo: item.userImage && item.userImage.trim() !== "" ? item.userImage : UserImage,
          name: item.executiveName || item.user_fullname,
          designation: item.designation || item.user_designation || "Executive",
          conversion: item.paidConversions || 0,
          revenue: item.revenue || 0,
          incentive: item.incentiveEarned || 0,
        }));

        setLeaderboardData(mappedData);
      } else {
        setLeaderboardData([]);
      }
    } catch (err) {
      console.error("Failed to fetch top performers", err);
      setLeaderboardData([]);
    }
  };

  const fetchExecutivePerformance = async () => {
    try {
      const token = localStorage.getItem("authToken");
      const query = getQueryParams();

      const response = await axios.get(`${API_URL}user-dashboard/stats-executive-performance${query}`, {
        headers: { Authorization: `Bearer ${token}`, Accept: "*/*" },
      });
      if (response.data && response.data.success) {
        setExecutivePerformanceData(response.data.data || []);
      } else {
        setExecutivePerformanceData([]);
      }
    } catch (err) {
      console.error("Failed to fetch executive performance", err);
      setExecutivePerformanceData([]);
    }
  };

  const fetchSalesMetrics = async () => {
    try {
      const token = localStorage.getItem("authToken");
      const query = getQueryParams();
      const response = await axios.get(`${API_URL}user-dashboard/sales-metrics${query}`, {
        headers: { Authorization: `Bearer ${token}`, Accept: "*/*" },
      });
      if (response.data && response.data.success) {
        setSalesMetrics(response.data.data || {});
      } else {
        setSalesMetrics({});
      }
    } catch (err) {
      console.error("Failed to fetch sales metrics", err);
      setSalesMetrics({});
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchFilteredData = async () => {
    try {
      setLoading(true);
      setLeaderboardData(null);
      setExecutivePerformanceData(null);
      await Promise.all([
        fetchStats(),
        fetchDisposition(),
        fetchTopPerformers(),
        fetchExecutivePerformance(),
        fetchSalesMetrics()
      ]);
    } catch (err) {
      console.error("Error fetching filtered data:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (startDate && endDate) {
      fetchFilteredData();
    } else if (!startDate && !endDate) {
      fetchFilteredData();
    }
  }, [startDate, endDate]);

  // Removed global loading spinner to allow progressive skeleton loading
  // if (loading) {
  //   return <LoadingSpinner />;
  // }

  const {
    subscription = {},
    subscriber = {},
    notification = {},
    snotification = {},
    services = [],
    servicelist = [],
    birthday = [],
    userType = 1, // Assuming you have user type in your response
    userCategory = 1, // Assuming you have user category in your response
  } = dashboardData || {};

  const teamTotals = {
    subscribed: stats?.totalSubscribedUsers ?? stats?.subscription?.total ?? subscription?.count ?? 0,
    totalLeads: stats?.totalLeads ?? stats?.leads?.total ?? 0,
    conversionRate: stats?.conversionRatePercent ?? stats?.conversion?.rate ?? 0,
    earnedCommission: stats?.totalCommission ?? stats?.commission?.total ?? 0,
  };

  const BAR_COLOR = "#127BBC";
  const PRIMARY_COLOR = "#348fe2";
  const WHITE_COLOR = "#ffffff";
  const PRIMARY_TRANSPARENT_2_COLOR = "rgba(52, 143, 226, 0.2)";

  const getBarChartData = (performance) => ({
    labels: performance.map((item) =>
      new Date(item.created_on).toLocaleString("default", { month: "short" })
    ),
    datasets: [
      {
        label: "Gains ₹",
        data: performance.map((item) => Number(item.gains.toFixed(2))),
        backgroundColor: BAR_COLOR,
        borderColor: BAR_COLOR,
        borderWidth: 1.5,
        pointBackgroundColor: WHITE_COLOR,
        pointBorderWidth: 1.5,
        pointRadius: 4,
        pointHoverBackgroundColor: PRIMARY_COLOR,
        pointHoverBorderColor: WHITE_COLOR,
        pointHoverRadius: 7,
      },
    ],
  });

  const barChartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    scales: { y: { beginAtZero: true } },
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: function (tooltipItem) {
            return "Gains : ₹" + tooltipItem.raw;
          },
        },
      },
    },
  };

  const servicePerformanceData = {
    labels: servicelist.map((s) => s.service_name),
    datasets: [
      {
        label: "Performance This Year",
        data: servicelist.map((s) => Number(s.gains.toFixed(2))),
        backgroundColor: PRIMARY_TRANSPARENT_2_COLOR,
        borderColor: PRIMARY_COLOR,
        borderWidth: 1.5,
        pointBackgroundColor: WHITE_COLOR,
        pointBorderWidth: 1.5,
        pointRadius: 4,
        pointHoverBackgroundColor: PRIMARY_COLOR,
        pointHoverBorderColor: WHITE_COLOR,
        pointHoverRadius: 7,
        fill: true,
      },
    ],
  };

  const lineChartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    scales: { y: { beginAtZero: true } },
  };

  const formatNumber = (value) => {
    if (!value || value === "" || value === "0") return "0.00";
    const num = Number(String(value).replace(/[^0-9.-]+/g, ""));
    if (isNaN(num)) return "0.00";
    return num.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };


  return (
    <div className="page-header-fixed page-sidebar-fixed fade in mt-5">
      <PermissionWrapper module="dashboard" action="view">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h1 className="page-header mb-0">
            Dashboard <small>Analysis</small>
          </h1>
          <PermissionWrapper module="dashboard_filters" action="view">
            <DateRangePicker
              initialSettings={{
                ranges: ranges,
                startDate: startDate || moment().subtract(6, "days"),
                endDate: endDate || moment(),
                locale: { format: "DD-MM-YYYY" },
              }}
              onApply={handleApply}
            >
              <div className="input-group" style={{ cursor: 'pointer', width: '240px' }}>
                <input
                  type="text"
                  className="form-control"
                  value={startDate && endDate ? `${startDate.format("DD-MM-YYYY")} - ${endDate.format("DD-MM-YYYY")}` : 'Select Date Range'}
                  readOnly
                  style={{ background: '#fff', cursor: 'pointer' }}
                />
                <span className="input-group-text bg-light">
                  <FontAwesomeIcon icon={faCalendarAlt} />
                </span>
              </div>
            </DateRangePicker>
          </PermissionWrapper>
        </div>
      </PermissionWrapper>

      {/* Top Widgets */}
      <PermissionWrapper module="dashboard_numbers" action="view">
        <div className="row mb-4">
          <div className="col-lg-3">
            <div className="widget widget-stats bg-gradient-blue-dark inverse-mode">
              <div className="widget-stats-left">
                <div className="widget-stats-title">Subscription Count</div>
              </div>
              <div className="widget-stats-right">
                <div className="widget-stats-value">{subscription?.count}</div>
              </div>
            </div>
          </div>
          <div className="col-lg-3">
            <div className="widget widget-stats bg-gradient-orange inverse-mode">
              <div className="widget-stats-left">
                <div className="widget-stats-title">Subscriber Count</div>
              </div>
              <div className="widget-stats-right">
                <div className="widget-stats-value">{subscriber?.count}</div>
              </div>
            </div>
          </div>
          <div className="col-lg-3">
            <div className="widget widget-stats bg-gradient-red inverse-mode">
              <div className="widget-stats-left">
                <div className="widget-stats-title">Notification Count</div>
              </div>
              <div className="widget-stats-right">
                <div className="widget-stats-value">{notification?.count}</div>
              </div>
            </div>
          </div>
          <div className="col-lg-3">
            <div className="widget widget-stats bg-gradient-green inverse-mode">
              <div className="widget-stats-left">
                <div className="widget-stats-title">Successful Notification Count</div>
              </div>
              <div className="widget-stats-right">
                <div className="widget-stats-value">{snotification?.count}</div>
              </div>
            </div>
          </div>
        </div>
      </PermissionWrapper>

      {/* Birthday Section - Only show if user has permission and is admin categories */}
      <PermissionWrapper module="dashboardpersonal" action="view">
        {(userType === 1 || userCategory === 1) && birthday && birthday.length > 0 && (
          <div className="row mb-4">
            <div className="col-lg-12">
              <div className="panel panel-default with-rounded-corner with-shadow">
                <div className="panel-body">
                  <h2 className="mb-4">🎉 Today's Birthday</h2>
                  {birthday.map((person, index) => (
                    <div className="birthday-card" key={index}>
                      <div className="d-flex justify-content-between">
                        <div>
                          <h5>{person.user_fullname}</h5>
                          <small>Position: {person.user_designation}</small>
                        </div>
                        <div className="text-end">
                          <span className="badge bg-success">🎂 Birthday Today!</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </PermissionWrapper>


      {/* 1. Weekly Leaderboard */}
      <PermissionWrapper module="dashboard_top_performers" action="view">
        <div className="row mb-5">
          <div className="col-12">
            <div className="leaderboard-container with-shadow">
              <div className="leaderboard-header">
                <h3 className="m-0"><FontAwesomeIcon icon={faCrown} className="me-2" /> Weekly Leaderboard</h3>
                <div className="badge bg-white text-primary">Top 10 Performers</div>
              </div>

              {leaderboardData === null ? (
                <LeaderboardSkeleton />
              ) : (
                <>

                  {(() => {
                    const safeData = Array.isArray(leaderboardData) ? leaderboardData : [];

                    // const topPerformer = safeData
                    //   .filter(item =>
                    //     Number(item.revenue) > 100000 &&
                    //     Number(item.conversion) > 2
                    //   )
                    //   .sort((a, b) => b.revenue - a.revenue)[0];

                    const CONVERSION_WEIGHT = 100000; // makes conversion meaningful vs revenue
                    const REVENUE_WEIGHT = 1;

                    const withScore = safeData.map(item => {
                      const conversion = Number(item.conversion) || 0;
                      const revenue = Number(item.revenue) || 0;

                      return {
                        ...item,
                        score: (conversion * CONVERSION_WEIGHT) + (revenue * REVENUE_WEIGHT)
                      };
                    });


                    const sortedByPerformance = withScore.sort(
                      (a, b) => b.score - a.score
                    );

                    const topPerformer = sortedByPerformance.find(
                      item =>
                        Number(item.revenue) > 100000 &&
                        Number(item.conversion) > 2
                    );


                    // const remainingList = topPerformer
                    //   ? safeData.filter(item => item !== topPerformer)
                    //   : safeData;

                    // tweak weights if leadership changes priority


                    const remainingList = topPerformer
                      ? sortedByPerformance.filter(item => item !== topPerformer)
                      : sortedByPerformance;

                    return (
                      <>


                        {/* Top 3 Podium */}
                        {topPerformer && (
                          <div className="top-performers-row" style={{ marginBottom: '30px' }}>
                            <div className="performer-card rank-1">
                              <div className="rank-text" style={{ color: '#fbbf24' }}>
                                Top Performer
                              </div>

                              <div className="avatar-wrapper large">
                                <img src={topPerformer.photo} className="avatar-img" alt="" />
                                <div
                                  className="rank-badge"
                                  style={{ backgroundColor: '#fbbf24', width: '35px', height: '35px' }}
                                >
                                  <FontAwesomeIcon icon={faCrown} />
                                </div>
                              </div>

                              <div className="performer-name">{topPerformer.name}</div>
                              <div className="performer-designation">{topPerformer.designation}</div>

                              <div className="revenue-badge bg-primary text-white d-flex flex-column align-items-center gap-1">
                                <span className="fw-bold fs-5">
                                  ₹{formatNumber(topPerformer.revenue)}
                                </span>
                                <span className="text-xs opacity-75 small">
                                  Incentive: ₹{formatNumber(topPerformer.incentive)}
                                </span>
                              </div>

                              <div className="mt-3 text-success small fw-bold">
                                <FontAwesomeIcon icon={faArrowTrendUp} className="me-1" />
                                Conversion: {topPerformer.conversion}
                              </div>
                            </div>
                          </div>
                        )}


                        <div className="clearfix"></div>
                        {/* List for Rank 4-10 */}
                        {/* <div className="leaderboard-list">
                    {leaderboardData.slice(1).map((item, idx) => (
                      <div className="leaderboard-item" key={item.id || idx}>
                        <div className="list-rank">{idx + 2}</div>
                        <img src={item.photo} className="list-avatar" alt="" />
                        <div className="list-info">
                          <p className="list-name">{item.name}</p>
                          <span className="list-designation">{item.designation}</span>
                        </div>
                        <div className="list-metrics">
                          <div className="metric">
                            <span className="metric-label">Conversion</span>
                            <span className="metric-value">{item.conversion}</span>
                          </div>
                          <div className="metric">
                            <span className="metric-label">Revenue</span>
                            <span className="metric-value">₹{formatNumber(item.revenue)}</span>
                          </div>
                          <div className="metric">
                            <span className="metric-label">Incentive</span>
                            <span className="metric-trend">₹{formatNumber(item.incentive)}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div> */}

                        <div className="leaderboard-list">
                          {remainingList.map((item, idx) => (
                            <div className="leaderboard-item" key={item.id || idx}>
                              <div className="list-rank">{topPerformer ? idx + 2 : idx + 1}</div>
                              <img src={item.photo} className="list-avatar" alt="" />

                              <div className="list-info">
                                <p className="list-name">{item.name}</p>
                                <span className="list-designation">{item.designation}</span>
                              </div>

                              <div className="list-metrics">
                                <div className="metric">
                                  <span className="metric-label">Conversion</span>
                                  <span className="metric-value">{item.conversion}</span>
                                </div>
                                <div className="metric">
                                  <span className="metric-label">Revenue</span>
                                  <span className="metric-value">
                                    ₹{formatNumber(item.revenue)}
                                  </span>
                                </div>
                                {/* <div className="metric">
                              <span className="metric-label">Incentive</span>
                              <span className="metric-trend">
                                ₹{formatNumber(item.incentive)}
                              </span>
                            </div> */}
                              </div>
                            </div>
                          ))}
                        </div>
                      </>
                    );
                  })()}
                </>
              )}
            </div>
          </div>
        </div>
      </PermissionWrapper>

      {/* 2. Executive Summary Metrics */}
      <PermissionWrapper module="dashboard_sales_metrics" action="view">
        <div className="row mb-5">
          <div className="col-12">
            <ExecutiveMetrics salesMetrics={salesMetrics} />
          </div>
        </div>
      </PermissionWrapper>

      {/* 3. Executive Performance Table */}
      <PermissionWrapper module="dashboard_executive_performance" action="view">
        <div className="row mb-5">
          <div className="col-12">
            <ExecutivePerformanceTable data={executivePerformanceData} />
          </div>
        </div>
      </PermissionWrapper>

      {/* 4. Team Overview Section */}
      {/* <PermissionWrapper module="dashboard_stats" action="view">
        <section className="mb-5">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2 className="text-lg font-semibold text-foreground mb-0 flex items-center gap-2">
              <Activity className="h-5 w-5 text-primary" />
              Team Overview
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              title="Total Subscriptions"
              value={teamTotals.subscribed.toLocaleString()}
              subtitle="This week"
              icon={Target}
              trend={{
                value: `${stats?.subscription?.weeklyPercent > 0 ? '+' : ''}${stats?.subscription?.weeklyPercent || 0}%`,
                isPositive: (stats?.subscription?.weeklyPercent || 0) >= 0
              }}
              variant="primary"
            />
            <StatCard
              title="Total Leads"
              value={teamTotals.totalLeads.toLocaleString()}
              subtitle="In pipeline"
              icon={Users}
              variant="default"
            />
            <StatCard
              title="Conversion Rate"
              value={`${teamTotals.conversionRate}%`}
              subtitle="Industry: 2-5%"
              icon={TrendingUp}
              trend={{
                value: `${stats?.conversion?.weeklyChange > 0 ? '+' : ''}${stats?.conversion?.weeklyChange || 0}%`,
                isPositive: (stats?.conversion?.weeklyChange || 0) >= 0
              }}
              variant="success"
            />
            <StatCard
              title="Total Commission"
              value={`₹${teamTotals.earnedCommission.toLocaleString()}`}
              subtitle="Earned this week"
              icon={IndianRupee}
              trend={{
                value: `${stats?.commission?.thisWeek >= 0 ? '+' : '-'}₹${Math.abs(stats?.commission?.thisWeek || 0).toLocaleString()}`,
                isPositive: (stats?.commission?.thisWeek || 0) >= 0
              }}
              variant="warning"
            />
          </div>
        </section>
      </PermissionWrapper> */}

      {/* 5. Lead Disposition Chart 
      <PermissionWrapper module="dashboard_disposition" action="view">
        <LeadDispositionChart dispositionData={dispositionData} />
      </PermissionWrapper>
      */}



      {/* Service Charts */}
      <PermissionWrapper module="dashboard_service_report" action="view">
        <div className="row">
          {dashboardData === null ? (
            <>
              <ChartSkeleton />
              <ChartSkeleton />
              <ChartSkeleton />
            </>
          ) : (
            services.map((service) => {
              const chartData = getBarChartData(service.performance || []);
              return (
                <div className="col-md-4 mainbox" key={service.id}>
                  <Link
                    to={`/service-report/${service.id}`}
                    className="boxlink"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <div className="panel panel-default with-rounded-corner with-shadow chartbox">
                      <div className="panel-heading">
                        <h6 className="title d-flex align-items-center justify-content-between">
                          <div className="d-flex align-items-center">
                            <img
                              src={service.service_image || DefaultImage}
                              alt={service.service_name}
                              style={{ width: "28px", marginRight: "8px" }}
                              onError={(e) => (e.target.src = DefaultImage)}
                            />
                            {service.service_name}
                          </div>
                          <span style={{ textAlign: "right", fontSize: "0.6rem", color: "#333333" }}>
                            Avg Profit / Trade
                            <br />
                            <span style={{ color: "#127BBC", fontSize: "0.7rem", fontWeight: "bold" }}>
                              ₹ {formatNumber(service.avgprofit) || 0}
                            </span>
                          </span>
                        </h6>
                      </div>
                      <div className="panel-body">
                        <Bar data={chartData} height={200} options={barChartOptions} />
                        <div className="row mt-2">
                          <div
                            className="progress"
                            style={{ width: "100%", height: "19px", paddingLeft: "0px", paddingRight: "0px", background: "lightgrey" }}
                          >
                            <div
                              className="progress-bar progress-bar-success"
                              role="progressbar"
                              aria-valuenow={service.accuracy || 0}
                              aria-valuemin="0"
                              aria-valuemax="100"
                              style={{
                                width: `${service.accuracy || 0}%`,
                                backgroundColor: "#6fca00",
                                padding: "5px",
                                color: "#333",
                              }}
                            >
                              {service.accuracy || 0}% Accuracy
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Link>
                </div>
              );
            })
          )}
        </div>

        {/* Performance By Services */}
        <div className="row">
          <div className="col-md-12">
            <div className="panel panel-default with-rounded-corner with-shadow">
              <div className="panel-heading">
                <h4 className="panel-title">Performance By Services</h4>
              </div>
              <div className="panel-body">
                <Line
                  data={servicePerformanceData}
                  height={100}
                  options={lineChartOptions}
                />
              </div>
            </div>
          </div>
        </div>
      </PermissionWrapper>
    </div>
  );
};

export default Dashboard;